/**
 * TwoFactorGate: what the app shell shows once the API has refused a
 * session for want of an authenticator code.
 *
 * Two people reach it. One has no authenticator app yet, and sets one up
 * here: a QR code for the app to scan, the key for an app that cannot,
 * and the first code to prove it took. The other already has one but
 * signed in on this browser before their Company required it, so this
 * session never gave a code; the only way to give one is to log in
 * again, which is where they are sent.
 *
 * Setting up happens between the browser and Firebase and needs nothing
 * from the API. The page reloads once it is done, so every request after
 * it carries the new token and a page that was refused loads as though it
 * never had been.
 */
import { FormEvent, useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { encode } from "uqr";
import { sendVerificationEmail } from "../lib/account-api";
import { useAuth, type AccountUser as User } from "../lib/auth";
import {
  confirmPassword,
  credentialFailure,
  emailConfirmed,
  hasAuthenticator,
  startAuthenticatorSetup,
  type AuthenticatorSetup,
} from "../lib/credentials";
import { errorMessage } from "../lib/error-message";
import { AuthPage } from "./AuthPage";
import { Banner } from "./ui/Banner";
import { Button } from "./ui/Button";
import styles from "./TwoFactorGate.module.css";

export function TwoFactorGate({ children }: { children: ReactNode }) {
  const { twoFactorRequired, user } = useAuth();
  if (!twoFactorRequired || !user) return <>{children}</>;
  if (hasAuthenticator(user)) {
    return <SignInAgain />;
  }
  return <TwoFactorSetup user={user} />;
}

function SignInAgain() {
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    const email = user?.email ?? "";
    void signOut().then(() =>
      navigate("/login", { replace: true, state: { signInAgain: true, email } }),
    );
    // Once, on the refusal that brought them here.
  }, []);
  return null;
}

type Step =
  | { kind: "starting" }
  | { kind: "password" }
  | { kind: "verify_email" }
  | { kind: "scan"; setup: AuthenticatorSetup }
  | { kind: "failed"; message: string };

export function TwoFactorSetup({ user }: { user: User }) {
  const { signOut } = useAuth();
  const [step, setStep] = useState<Step>({ kind: "starting" });

  const start = useCallback(async () => {
    setStep({ kind: "starting" });
    try {
      setStep({ kind: "scan", setup: await startAuthenticatorSetup(user) });
    } catch (err) {
      const failure = credentialFailure(err);
      if (failure === "needs_recent_sign_in") {
        setStep({ kind: "password" });
      } else if (failure === "unverified_email") {
        setStep({ kind: "verify_email" });
      } else {
        setStep({
          kind: "failed",
          message: "Could not start setting up your authenticator app. Try again.",
        });
      }
    }
  }, [user]);

  useEffect(() => {
    void start();
  }, [start]);

  const logOut = (
    <p className={styles.footNote}>
      <Button size="sm" type="button" variant="link" onClick={() => void signOut()}>
        Log out
      </Button>
    </p>
  );

  return (
    <AuthPage
      title="Set up two-factor"
      description="Your company requires a code from an authenticator app, such as Google Authenticator or 1Password, each time you log in."
    >
      <div className={styles.stack}>
        {step.kind === "starting" && (
          <p className={styles.loading}>Loading</p>
        )}
        {step.kind === "password" && (
          <ConfirmPassword user={user} onConfirmed={() => void start()} />
        )}
        {step.kind === "verify_email" && (
          <VerifyEmail user={user} onVerified={() => void start()} />
        )}
        {step.kind === "scan" && <Scan setup={step.setup} />}
        {step.kind === "failed" && (
          <>
            <Banner variant="error">{step.message}</Banner>
            <Button
              className={styles.submit}
              type="button"
              onClick={() => void start()}
            >
              Try again
            </Button>
          </>
        )}
        {logOut}
      </div>
    </AuthPage>
  );
}

function Scan({ setup }: { setup: AuthenticatorSetup }) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await setup.enroll(code);
      window.location.reload();
    } catch (err) {
      setError(
        credentialFailure(err) === "wrong_code"
          ? "That code did not match. Enter the one your app shows now."
          : "Could not turn two-factor on. Try again.",
      );
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className={styles.form}>
      <div className={styles.qrBox}>
        <QrCode value={setup.qrUrl} />
        <p className={styles.qrHelp}>
          Scan this with your authenticator app. Can&apos;t scan it? Enter this
          key instead:
        </p>
        <code className={styles.secretKey}>{setup.key}</code>
      </div>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>6-digit code from the app</span>
        <input
          autoComplete="one-time-code"
          className={styles.input}
          disabled={submitting}
          inputMode="numeric"
          maxLength={8}
          onChange={(event) => setCode(event.target.value)}
          required
          value={code}
        />
      </label>

      {error ? <Banner variant="error">{error}</Banner> : null}

      <Button
        className={styles.submit}
        disabled={submitting}
        loading={submitting}
        type="submit"
      >
        Turn on
      </Button>
    </form>
  );
}

/** A session open a while proves it is still its owner before a second factor. */
function ConfirmPassword({
  user,
  onConfirmed,
}: {
  user: User;
  onConfirmed: () => void;
}) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await confirmPassword(user, password);
      onConfirmed();
    } catch (err) {
      setError(
        credentialFailure(err) === "wrong_password"
          ? "That password is incorrect."
          : "Could not check your password. Try again.",
      );
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className={styles.form}>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Enter your password to continue</span>
        <input
          autoComplete="current-password"
          autoFocus
          className={styles.input}
          disabled={submitting}
          onChange={(event) => setPassword(event.target.value)}
          required
          type="password"
          value={password}
        />
      </label>
      {error ? <Banner variant="error">{error}</Banner> : null}
      <Button
        className={styles.submit}
        disabled={submitting}
        loading={submitting}
        type="submit"
      >
        Continue
      </Button>
    </form>
  );
}

/**
 * Firebase will not add a second factor to an address nobody has shown is
 * theirs. An invited account starts unverified, so the first setup may
 * have to send a verification email and wait for it to be opened.
 */
function VerifyEmail({
  user,
  onVerified,
}: {
  user: User;
  onVerified: () => void;
}) {
  const [sent, setSent] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setError(null);
    setWorking(true);
    try {
      await sendVerificationEmail();
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setWorking(false);
    }
  }

  async function check() {
    setError(null);
    setWorking(true);
    try {
      if (await emailConfirmed(user)) {
        onVerified();
        return;
      }
      setError("That address is not confirmed yet. Open the link in the email first.");
    } catch {
      setError("Could not check. Try again.");
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className={styles.stack}>
      <Banner variant="info">
        {sent
          ? `We sent a link to ${user.email}. Open it, then come back and continue.`
          : `First, confirm that ${user.email} is your address.`}
      </Banner>
      {error ? <Banner variant="error">{error}</Banner> : null}
      <Button
        className={styles.submit}
        loading={working}
        disabled={working}
        type="button"
        onClick={() => void (sent ? check() : send())}
      >
        {sent ? "Continue" : "Send confirmation email"}
      </Button>
      {sent ? (
        <p className={styles.footNote}>
          <Button
            size="sm"
            type="button"
            variant="link"
            disabled={working}
            onClick={() => void send()}
          >
            Send it again
          </Button>
        </p>
      ) : null}
    </div>
  );
}

/**
 * The otpauth:// link drawn as a QR code: one SVG rect per dark module,
 * from uqr's matrix, so no markup string is injected into the page.
 */
export function QrCode({ value }: { value: string }) {
  const { size, data } = encode(value, { ecc: "M", border: 2 });
  const cells: ReactNode[] = [];
  data.forEach((row, y) =>
    row.forEach((dark, x) => {
      if (dark) cells.push(<rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} />);
    }),
  );
  return (
    <svg
      role="img"
      aria-label="QR code for your authenticator app"
      viewBox={`0 0 ${size} ${size}`}
      className={styles.qr}
      shapeRendering="crispEdges"
    >
      <rect width={size} height={size} fill="#fff" />
      <g fill="#000">{cells}</g>
    </svg>
  );
}
